import type { FC, ReactNode } from 'react'

import { useEffect, useMemo, useRef, useState } from 'react'
import { animate, useInView } from 'motion/react'

interface CountUpProps {
    value: string
    className?: string
    duration?: number
}

interface ParsedValue {
    prefix: string
    number: number
    suffix: string
    decimals: number
    grouped: boolean
}

const parseValue = (value: string): ParsedValue | null => {
    const match = value.match(/^([^\d]*)([\d][\d,]*(?:\.\d+)?)(.*)$/)

    if (!match) return null

    const raw = match[2].replace(/,/g, '')

    return {
        prefix: match[1],
        number: Number.parseFloat(raw),
        suffix: match[3],
        decimals: raw.includes('.') ? raw.split('.')[1].length : 0,
        grouped: match[2].includes(',')
    }
}

// Counts a figure up from zero the first time it scrolls into view.
// The server renders the final value, so it is never missing without JavaScript.
const CountUp: FC<CountUpProps> = ({ value, className, duration = 1.8 }): ReactNode => {
    const ref = useRef<HTMLSpanElement>(null)
    const started = useRef(false)
    const parsed = useMemo(() => parseValue(value), [value])
    const inView = useInView(ref, { once: true, margin: '0px 0px -10% 0px' })
    const [display, setDisplay] = useState(value)

    const format = (current: number): string => {
        if (!parsed) return value

        const fixed = current.toFixed(parsed.decimals)
        const shown = parsed.grouped
            ? Number(fixed).toLocaleString('en-US', { minimumFractionDigits: parsed.decimals, maximumFractionDigits: parsed.decimals })
            : fixed

        return `${parsed.prefix}${shown}${parsed.suffix}`
    }

    useEffect(() => {
        if (!parsed || started.current) return

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            started.current = true

            return
        }

        if (!inView) {
            setDisplay(format(0))

            return
        }

        started.current = true

        const controls = animate(0, parsed.number, {
            duration,
            ease: [0.22, 1, 0.36, 1],
            onUpdate: (latest) => setDisplay(format(latest))
        })

        return () => controls.stop()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inView, parsed])

    return (
        <span ref={ref} className={className}>
            {display}
        </span>
    )
}

export default CountUp
